import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, LifeBuoy, ScrollText, LogOut, Trash2, UserRound, ArrowRightLeft, ShieldCheck, Loader2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import PersonaVerify from "../components/PersonaVerify";
import VerifiedBadge from "../components/VerifiedBadge";

// Identity verification card — Persona-backed, server-authoritative. The badge
// renders only when /auth/me says id_verified; starting verification reuses
// the same embedded Persona flow as checkout.
function IdentityCard() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [required, setRequired] = useState(true); // admin master switch — server-authoritative
  useEffect(() => {
    api.personaStatus().then((s) => setRequired(s.required !== false)).catch(() => {});
  }, []);
  return (
    <div className="card-dark mt-6 p-6" data-testid="identity-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-[#E50914]/15 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-5 h-5 text-[#FF3B45]" />
          </span>
          <div>
            <p className="text-sm font-semibold">Identity verification</p>
            <p className="text-xs text-zinc-500">Required before booking or publishing parking.</p>
          </div>
        </div>
        {user?.id_verified ? (
          <VerifiedBadge verified testId="profile-id-verified-badge" />
        ) : !required ? (
          <span className="text-xs text-zinc-500" data-testid="identity-not-required">Not currently required</span>
        ) : (
          <button onClick={() => setOpen((o) => !o)} className="btn-primary text-xs !py-2" data-testid="verify-identity-btn">
            Verify Identity
          </button>
        )}
      </div>
      {open && !user?.id_verified && required && (
        <div className="mt-5 pt-5 border-t border-[#27272A]">
          <PersonaVerify onVerified={async () => { setOpen(false); await refresh(); }} onClose={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}
import { useAuth } from "../context/AuthContext";
import { setPageTitle } from "../lib/format";

function SecuritySection() {
  const { user, refresh } = useAuth();
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const hasPassword = (user?.auth_providers || []).includes("password");

  const change = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) { toast.error("New passwords do not match"); return; }
    setBusy(true);
    try {
      const r = await api.changePassword({ current_password: f.current_password, new_password: f.new_password });
      toast.success(r.message || "Password updated");
      setF({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const resendVerify = async () => {
    setVerifyBusy(true);
    try { const r = await api.verifyEmailRequest(); toast.success(r.message || "Verification email sent"); }
    catch (e) { toast.error(e.message); } finally { setVerifyBusy(false); }
  };

  return (
    <div className="card-dark mt-6 p-6" data-testid="security-section">
      <h2 className="font-bold text-sm uppercase tracking-widest text-zinc-400 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-[#FF3B45]" /> Security
      </h2>
      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-zinc-300">Email verification</p>
        {user?.email_verified ? (
          <span className="text-xs font-bold text-emerald-400" data-testid="email-verified-badge">Verified</span>
        ) : hasPassword ? (
          <button onClick={resendVerify} disabled={verifyBusy} className="btn-secondary text-xs !py-2" data-testid="send-verification-btn">
            {verifyBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Send verification email
          </button>
        ) : (
          <span className="text-xs text-zinc-500" data-testid="email-verified-badge">Handled by your sign-in provider</span>
        )}
      </div>
      {hasPassword && (
        <form onSubmit={change} className="mt-6 pt-6 border-t border-[#27272A] space-y-3" data-testid="change-password-form">
          <p className="text-sm text-zinc-300 font-semibold">Change password</p>
          <input type="password" required value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })}
                 placeholder="Current password" className="input-dark" data-testid="current-password-input" aria-label="Current password" autoComplete="current-password" />
          <input type="password" required minLength={8} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })}
                 placeholder="New password (8+ characters)" className="input-dark" data-testid="new-password-input" aria-label="New password" autoComplete="new-password" />
          <input type="password" required value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })}
                 placeholder="Confirm new password" className="input-dark" data-testid="confirm-password-input" aria-label="Confirm new password" autoComplete="new-password" />
          <p className="text-xs text-zinc-600">Changing your password signs out all other sessions.</p>
          <button type="submit" disabled={busy} className="btn-primary text-sm" data-testid="change-password-btn">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Update password
          </button>
        </form>
      )}
    </div>
  );
}

function NotificationPrefsCard() {
  const [prefs, setPrefs] = useState(null);
  useEffect(() => { api.getNotifPrefs().then(setPrefs).catch(() => {}); }, []);
  if (!prefs) return null;
  const toggle = async (key) => {
    const prev = prefs;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try { await api.updateNotifPrefs({ [key]: next[key] }); }
    catch (e) { toast.error(e.message); setPrefs(prev); }
  };
  const Row = ({ k, label, hint }) => (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <div>
        <p className="text-sm text-zinc-300">{label}</p>
        <p className="text-xs text-zinc-600 mt-0.5">{hint}</p>
      </div>
      <button onClick={() => toggle(k)} role="switch" aria-checked={prefs[k]} data-testid={`pref-${k.replace(/_/g, "-")}`}
              className={`w-11 h-6 rounded-full transition-colors relative shrink-0 ${prefs[k] ? "bg-[#E50914]" : "bg-[#27272A]"}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${prefs[k] ? "translate-x-[22px]" : "translate-x-0.5"}`} />
      </button>
    </div>
  );
  return (
    <div className="card-dark mt-6 p-6" data-testid="notif-prefs-card">
      <h2 className="font-bold text-sm uppercase tracking-widest text-zinc-400 flex items-center gap-2">
        <Bell className="w-4 h-4 text-[#FF3B45]" /> Email notifications
      </h2>
      <div className="mt-2 divide-y divide-[#27272A]">
        <Row k="booking_emails" label="Bookings & listings" hint="Confirmations, arrivals, session updates, listing reviews" />
        <Row k="reminder_emails" label="Reminders" hint="Parking ending soon, monthly renewals" />
      </div>
      <p className="text-[11px] text-zinc-600 mt-3">Security and account emails (password changes, verification) are always sent.</p>
    </div>
  );
}

export default function Profile() {
  const { user, logout, refresh, role } = useAuth();
  const [delOpen, setDelOpen] = useState(false);
  const [delPw, setDelPw] = useState("");
  const [delText, setDelText] = useState("");
  const [delReason, setDelReason] = useState("");
  const [delBlockers, setDelBlockers] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  useEffect(() => setPageTitle("Profile"), []);

  const switchMode = async () => {
    const next = user.mode === "host" ? "driver" : "host";
    try {
      await api.setMode(next);
      await refresh();
      toast.success(`Switched to ${next} mode`);
      navigate(next === "host" ? "/host" : "/dashboard");
    } catch (e) { toast.error(e.message); }
  };

  const deleteAccount = async () => {
    setBusy(true);
    setDelBlockers(null);
    try {
      await api.deleteAccount({
        password: delPw || undefined,
        confirm_text: delText || undefined,
        reason: delReason || undefined,
      });
      toast.success("Account deletion scheduled — your access is now disabled.");
      window.location.replace("/");
    } catch (e) {
      if (e.detail?.blockers) setDelBlockers(e.detail.blockers);
      else toast.error(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="profile-page">
      <Navbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <p className="section-label">Account</p>
        <h1 className="type-display mt-3">Profile &amp; Settings</h1>

        <div className="card-dark p-6 mt-8 flex items-center gap-4" data-testid="profile-card">
          <span className="w-14 h-14 rounded-full bg-[#E50914]/20 text-[#FF3B45] flex items-center justify-center text-xl font-bold">
            {(user?.name || user?.email || "U")[0].toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="font-bold text-lg truncate" data-testid="profile-name">{user?.name}</p>
            <p className="text-sm text-zinc-400 truncate" data-testid="profile-email">{user?.email}</p>
            <p className="text-xs text-zinc-500 mt-1 capitalize">Role: {role} · Mode: {user?.mode || "driver"}</p>
          </div>
        </div>

        <IdentityCard />
        <div className="card-dark mt-6 divide-y divide-[#27272A]" data-testid="settings-list">
          <button onClick={switchMode} data-testid="switch-mode-btn" className="w-full flex items-center gap-4 p-5 hover:bg-[#27272A]/50 transition-colors text-left">
            <ArrowRightLeft className="w-5 h-5 text-[#FF3B45]" />
            <div><p className="font-semibold text-sm">Switch to {user?.mode === "host" ? "Driver" : "Host"} mode</p>
              <p className="text-xs text-zinc-500 mt-0.5">Toggle between finding parking and hosting</p></div>
          </button>
          <Link to="/notifications" data-testid="profile-notifications-link" className="flex items-center gap-4 p-5 hover:bg-[#27272A]/50 transition-colors">
            <Bell className="w-5 h-5 text-[#FF3B45]" />
            <div><p className="font-semibold text-sm">Notifications</p><p className="text-xs text-zinc-500 mt-0.5">Booking, payment and support updates</p></div>
          </Link>
          <Link to="/support" data-testid="profile-support-link" className="flex items-center gap-4 p-5 hover:bg-[#27272A]/50 transition-colors">
            <LifeBuoy className="w-5 h-5 text-[#FF3B45]" />
            <div><p className="font-semibold text-sm">Support</p><p className="text-xs text-zinc-500 mt-0.5">AI help + human tickets</p></div>
          </Link>
          <Link to="/legal" data-testid="profile-legal-link" className="flex items-center gap-4 p-5 hover:bg-[#27272A]/50 transition-colors">
            <ScrollText className="w-5 h-5 text-[#FF3B45]" />
            <div><p className="font-semibold text-sm">Legal</p><p className="text-xs text-zinc-500 mt-0.5">Terms, privacy, company information</p></div>
          </Link>
        </div>

        <SecuritySection />
        <NotificationPrefsCard />

        <div className="card-dark mt-6 p-6" data-testid="account-actions">
          <h2 className="font-bold text-sm uppercase tracking-widest text-zinc-400">Account</h2>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <button onClick={logout} data-testid="profile-logout-btn" className="btn-secondary text-sm">
              <LogOut className="w-4 h-4" /> Log Out
            </button>
            <button onClick={() => { setDelOpen(true); setDelBlockers(null); }} data-testid="delete-account-btn"
                    className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-red-400 border border-red-500/30 hover:bg-red-500/10 rounded-full px-6 py-3 transition-colors">
              <Trash2 className="w-4 h-4" /> Delete Account
            </button>
          </div>
        </div>

        <p className="mt-8 text-xs text-zinc-600 flex items-center gap-2"><UserRound className="w-3.5 h-3.5" /> Member since {user?.created_at ? new Date(user.created_at).toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "—"}</p>

        {delOpen && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="delete-account-modal">
            <div className="card-dark w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
              <p className="font-display font-bold text-lg">Delete your MyRedPark account?</p>
              <p className="text-xs text-zinc-400 mt-3 leading-relaxed">
                Deleting your account will disable your access to MyRedPark. Your eligible profile and personal data will be scheduled for deletion after 30 days. Some payment, booking, tax, invoice, fraud-prevention and audit records may need to be retained or de-identified to meet legal and operational requirements. Your active bookings and listings must be resolved before you can proceed.
              </p>
              <textarea value={delReason} onChange={(e) => setDelReason(e.target.value)} maxLength={500} rows={2}
                        placeholder="Reason (optional)" className="input-dark mt-4 !h-auto" data-testid="delete-account-reason" />
              {user?.has_password ? (
                <input type="password" value={delPw} onChange={(e) => setDelPw(e.target.value)} autoComplete="current-password"
                       placeholder="Re-enter your password to confirm" className="input-dark mt-3" data-testid="delete-account-password" />
              ) : (
                <input value={delText} onChange={(e) => setDelText(e.target.value)}
                       placeholder="Type DELETE to confirm" className="input-dark mt-3" data-testid="delete-account-confirm-text" />
              )}
              {delBlockers && (
                <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3.5" data-testid="delete-blockers">
                  <p className="text-xs font-semibold text-amber-300">Your account can&apos;t be deleted yet — resolve these first:</p>
                  <ul className="mt-2 space-y-1.5">
                    {delBlockers.map((b) => (
                      <li key={b.kind} className="text-xs text-zinc-300 leading-relaxed" data-testid={`delete-blocker-${b.kind}`}>• {b.label}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="mt-5 flex gap-3">
                <button onClick={() => setDelOpen(false)} className="btn-secondary flex-1 text-sm" data-testid="delete-account-cancel-btn">Cancel</button>
                <button onClick={deleteAccount} disabled={busy || (user?.has_password ? !delPw : delText !== "DELETE")}
                        className="flex-1 text-sm font-bold bg-[#E50914] hover:bg-[#FF3B45] text-white rounded-full px-4 py-2.5 transition-colors disabled:opacity-50"
                        data-testid="delete-account-confirm-btn">
                  {busy ? "Deleting…" : "Yes, Delete My Account"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
